/* eslint-disable max-lines-per-function */
import React, { useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { InputTypeAhead, FormikInputMoney, ButtonSizeOptions, LabelText } from "@loanbossinc/component-library";
import { withFormik } from "formik";
import { FormControlLabel, CircularProgress, Radio } from "@material-ui/core";

import MultiSelectInput from "./multiSelectInput";
import {
  VacancyRate,
  VacancyRateHeaderLeft,
  VacancyRateHeaderWrapper,
  VacancyRateWrapper,
  PropertyNameSmall,
  ParametersRowWrapper,
  ParametersRowLeft,
  ParametersRowRight,
  StyledInputCheckbox,
  StyledInputNumber,
  StyledPrimaryButton,
  StyledRadioGroup,
  StyledToggleSelect
} from "./rentRollFieldStyles";

export const CALCULATION_PERIOD_OPTIONS = [
  { value: "trailing1month", name: "Trailing 1-month", monthAmount: 12, type: "trailing" },
  { value: "trailing3month", name: "Trailing 3-month", monthAmount: 12, type: "trailing" },
  { value: "trailing6month", name: "Trailing 6-month", monthAmount: 12, type: "trailing" },
  { value: "trailing12month", name: "Trailing 12-month", monthAmount: 12, type: "trailing" },
  { value: "trending1month", name: "Trending 1-month", monthAmount: 12, type: "trending" },
  { value: "trending3month", name: "Trending 3-month", monthAmount: 12, type: "trending" },
  { value: "trending6month", name: "Trending 6-month", monthAmount: 12, type: "trending" },
  { value: "trending12month", name: "Trending 12-month", monthAmount: 12, type: "trending" }
];

const MARKET_RENT_OPTIONS = [
  { value: "manualEntry", name: "Manual Entry" },
  { value: "simpleAverage", name: "Simple Average" },
  { value: "weightedAverage", name: "Weighted Average" }
];

const VACANT_SUITE_PREFERENCE_OPTIONS = [
  { value: "leaveVacant", name: "Leave Vacant" },
  { value: "useMarketRent", name: "Use Market Rent" }
];

const isOverrideVacantIncludedRadioOptions = [
  { value: true, label: "Included" },
  { value: false, label: "Excluded" }
];

const DEFAULT_INCLUDE_OPTIONS = [
  { name: "Exclude all", index: 0, value: false },
  { name: "Include all", index: 1, value: true }
];

const STRAIGHT_LINE_CALCULATION_TYPE_OPTIONS = [
  { name: "Rent", index: 0, value: false },
  { name: "Straight-Line", index: 1, value: true }
];

const SelectCalculationParameters = props => {
  const {
    calculationPeriod,
    deal,
    isSavingField,
    manualEntryRentPerSqFt,
    marketRentType,
    maxMonthValidation,
    overrideOccupiedMonths,
    overrideVacantMonths,
    selectedField,
    selectedProperty,
    selectedRentRollField,
    setRentRollFieldSelections,
    shouldOverrideOccupied,
    shouldOverrideVacant,
    isOverrideVacantIncluded,
    vacantSuiteDefault,
    getRentRollChargeCodes,
    rentRollChargeCodes,
    selectedChargeCodes,
    selectedFreeRentChargeCodes,
    isChargeCodesLoading,
    chargeCodeAliases,
    isChargeCodesAliasesLoading,
    isMonthInputValid,
    showConfirmationModal,
    handleNext,
    isNextDisabled,
    defaultInclude,
    allFilesForDeal,
    shouldReplaceRentRollStepByMonths,
    replaceRentRollStepByMonths,
    shouldUseFreeRentChargeCodeByMonths,
    freeRentChargeCodeByMonths,
    isStraightLine
  } = props;



  if (selectedFreeRentChargeCodes === null && (selectedField || selectedRentRollField)) {
    const currentField = selectedField || selectedRentRollField;
    setRentRollFieldSelections({ selectedFreeRentChargeCodes: currentField.includedFreeRentChargeCodes });
  }

  if (selectedChargeCodes === null && (selectedField || selectedRentRollField)) {
    const currentField = selectedField || selectedRentRollField;
    setRentRollFieldSelections({ selectedChargeCodes: currentField.includedRentChargeCodes });
  }

  if (!vacantSuiteDefault && (selectedField || selectedRentRollField)) {
    const currentField = (selectedField || selectedRentRollField);
    setRentRollFieldSelections({ vacantSuiteDefault: currentField.vacantSuiteDefault });
  }

  useEffect(() => {
    if (!rentRollChargeCodes) {
      getRentRollChargeCodes({ dealId: deal.id, propertyId: selectedProperty.id });
    }
  }, [rentRollChargeCodes, deal, selectedProperty]);

  useEffect(() => {
    if (rentRollChargeCodes && !selectedChargeCodes) {
      setRentRollFieldSelections({ selectedChargeCodes: mapCodesToAliases(rentRollChargeCodes.map(x => x.chargeCode)), isAnythingWasUpdated: true });
    }
  }, [rentRollChargeCodes]);

  useEffect(() => {
    if (!shouldUseFreeRentChargeCodeByMonths && selectedFreeRentChargeCodes && selectedFreeRentChargeCodes.length > 0){
      setRentRollFieldSelections({ selectedFreeRentChargeCodes: [], isAnythingWasUpdated: true });
    }
  },[shouldUseFreeRentChargeCodeByMonths, selectedFreeRentChargeCodes]);

  const companyId = deal.owningCompanyId;

  const setCalculationPeriod = value => {
    if (value) {
      const { monthAmount } = CALCULATION_PERIOD_OPTIONS.find(opt => opt.value === value);
      const updatedState = { calculationPeriod: value, maxMonthValidation: monthAmount, isAnythingWasUpdated: true };
      setRentRollFieldSelections(updatedState);
    } else {
      setRentRollFieldSelections({
        calculationPeriod: null,
        maxMonthValidation: null,
        shouldOverrideOccupied: false,
        overrideOccupiedMonths: null,
        isOverrideVacantIncluded: true,
        isAnythingWasUpdated: true
      });
    }
  };

  const setVacantSuiteDefault = value => {
    if (value) {
      setRentRollFieldSelections({ vacantSuiteDefault: value, isAnythingWasUpdated: true });
    } else {
      setRentRollFieldSelections({ vacantSuiteDefault: null, isAnythingWasUpdated: true });
    }
  };

  const getMarketRentOptions = () => {
    const calculationTypes = [
      { value: "manualEntry", name: "Manual Entry" },
      { value: "simpleAverage", name: "Simple Average" },
      { value: "weightedAverage", name: "Weighted Average" }
    ];

    if ( allFilesForDeal ){
      const filesArray = Array.isArray(allFilesForDeal) ? allFilesForDeal : [allFilesForDeal];
      const mfRentRollFile = filesArray.find(file => file && file.typeId === "mf-rent-roll");
  
      if (mfRentRollFile) {   
        calculationTypes.push(
          { value: "fromRentRoll", name: "From Rent Roll" }
        );
      }
    }

    return calculationTypes;
  };

  const setMarketRentType = value => {
    if (value) {
      setRentRollFieldSelections({ marketRentType: value, isAnythingWasUpdated: true });
      if (value !== MARKET_RENT_OPTIONS[0].value) {
        setRentRollFieldSelections({ manualEntryRentPerSqFt: null });
      }
    } else {
      setRentRollFieldSelections({ marketRentType: null, isAnythingWasUpdated: true });
    }
  };

  const getFieldNameByCheckedBox = targetName => {
    switch(targetName) {
        case "shouldOverrideOccupied":
          return "overrideOccupiedMonths";
        case "shouldOverrideVacant":
          return "overrideVacantMonths";
        case "shouldReplaceRentRollStepByMonths":
          return "replaceRentRollStepByMonths";
        case "shouldUseFreeRentChargeCodeByMonths":
          return "freeRentChargeCodeByMonths";
        default:
          return "";
    }
  };

  const setOverrideCheckbox = e => {
    if (!e.target.checked) {
      const inputToClear = getFieldNameByCheckedBox(e.target.name);
      setRentRollFieldSelections({ [inputToClear]: null, [e.target.name]: e.target.checked, isAnythingWasUpdated: true });
    } else {
      setRentRollFieldSelections({ [e.target.name]: e.target.checked, isAnythingWasUpdated: true });
    }
  };

  const formatInput = (type, val) => {
    switch (type) {
      case "integer":
        return parseInt(val, 10);
      case "money":
        return parseFloat(val);
      case "percent":
        return parseFloat(val) / 100;
      default:
        return null;
    }
  };

  const handleInputEntry = (e, type) => {
    const convertedInput = formatInput(type, e.target.value);
    setRentRollFieldSelections({ [e.target.name]: convertedInput, isAnythingWasUpdated: true });
  };

  const handleOverrideVacantRadioChange = e => {
    const newValue = e.target.value === "true";
    setRentRollFieldSelections({ isOverrideVacantIncluded: newValue, isAnythingWasUpdated: true });
  };

  const getMonthErrorMessage = type => {
    const isValid = isMonthInputValid(type);
    if (!isValid) {
      return (
        <div style={{ position: "absolute", color: "#FF1744", fontSize: "12px", top: "-20px", fontFamily: "Roboto" }}>
          {`Valid months are 0 - ${maxMonthValidation}`}
        </div>
      );
    }

    return null;
  };

  const onChargeCodeSelectChange = e => {
    const selectedChargeCodeOrAlias = e.target.value;
    const mappedAliasesToChargeCodes = selectedChargeCodeOrAlias.map(codeOrAlias => {
      const aliasObj = chargeCodeAliases && chargeCodeAliases.find(alias => alias.alias === codeOrAlias);

      return aliasObj ? aliasObj.chargeCode : codeOrAlias;
    });
    setRentRollFieldSelections({ selectedChargeCodes: mappedAliasesToChargeCodes, isAnythingWasUpdated: true });
  };

  const onFreeRentChargeCodeSelectChange = e => {
    const selectedChargeCodeOrAlias = e.target.value;
    const mappedAliasesToChargeCodes = selectedChargeCodeOrAlias.map(codeOrAlias => {
      const aliasObj = chargeCodeAliases && chargeCodeAliases.find(alias => alias.alias === codeOrAlias);

      return aliasObj ? aliasObj.chargeCode : codeOrAlias;
    });
    setRentRollFieldSelections({ selectedFreeRentChargeCodes: mappedAliasesToChargeCodes, isAnythingWasUpdated: true });
  };

  const handleOperatorChange = option => {
    const selected = option || DEFAULT_INCLUDE_OPTIONS[0];
    setRentRollFieldSelections({
      defaultInclude: selected.value,
      isAnythingWasUpdated: true
    });
  };

  const handleIsStraightLine = option => {
    const selected = option || STRAIGHT_LINE_CALCULATION_TYPE_OPTIONS[0];
    setRentRollFieldSelections({
      isStraightLine: selected.value,
      isAnythingWasUpdated: true
    });
  };

  const mapCodesToAliases = codes => codes && codes.map(code => {
    let aliasObj;
    if (code.chargeCode && chargeCodeAliases) {
      aliasObj = chargeCodeAliases.find(alias => alias.chargeCode === code.chargeCode);

      return aliasObj ? { chargeCode: aliasObj.alias, chargeType: code.chargeType } : code;
    }
    aliasObj = chargeCodeAliases && chargeCodeAliases.find(alias => alias.chargeCode === code);

    return aliasObj ? aliasObj.alias : code;
  }) || [];

  return (
    <VacancyRateWrapper>
      <VacancyRateHeaderWrapper>
        <VacancyRateHeaderLeft>
          <VacancyRate>Rent Roll Calculation Parameters</VacancyRate>
          <PropertyNameSmall>{selectedProperty.name}</PropertyNameSmall>
        </VacancyRateHeaderLeft>
      </VacancyRateHeaderWrapper>
      <ParametersRowWrapper>
        <ParametersRowLeft>Calculation Type</ParametersRowLeft>
        <ParametersRowRight>
          <StyledToggleSelect options={STRAIGHT_LINE_CALCULATION_TYPE_OPTIONS} onChange={handleIsStraightLine} defaultIndex={isStraightLine ? 1 : 0} />
        </ParametersRowRight>
      </ParametersRowWrapper>
      <ParametersRowWrapper>
        <ParametersRowLeft>Calculation Period</ParametersRowLeft>
        <ParametersRowRight>
          <InputTypeAhead
            variant="filled"
            id="calculationPeriod"
            name="Period"
            label={!calculationPeriod ? "Select Period" : "Period"}
            options={CALCULATION_PERIOD_OPTIONS}
            displayWith={option => option.name}
            setValueWith={option => option.value}
            onChange={setCalculationPeriod}
            value={calculationPeriod}
          />
        </ParametersRowRight>
      </ParametersRowWrapper>
      <ParametersRowWrapper>
        <ParametersRowLeft>Default lease preference to:</ParametersRowLeft>
        <ParametersRowRight>
          <StyledToggleSelect options={DEFAULT_INCLUDE_OPTIONS} onChange={handleOperatorChange} defaultIndex={defaultInclude ? 1 : 0} />
        </ParametersRowRight>
      </ParametersRowWrapper>
      <ParametersRowWrapper>
        <ParametersRowLeft>
          <FormControlLabel
            control={
              <StyledInputCheckbox
                checked={shouldOverrideOccupied}
                onChange={setOverrideCheckbox}
                name="shouldOverrideOccupied"
                value={shouldOverrideOccupied}
              />
            }
            label={
              <p style={{ paddingLeft: "0.5em", marginBlockStart: "0", marginBlockEnd: "0", fontFamily: "Work Sans", fontSize: "14px" }}>
                Leases that start within X months of Calculation Date
                {" "}
                <br />
                {" "}
                are considered included
              </p>
            }
            labelPlacement="end"
          />
        </ParametersRowLeft>
        <ParametersRowRight>
          {getMonthErrorMessage("occupied")}
          <StyledInputNumber
            isError={!isMonthInputValid("occupied")}
            name="overrideOccupiedMonths"
            label="Number of months"
            variant="filled"
            value={overrideOccupiedMonths || ""}
            onChange={e => handleInputEntry(e, "integer")}
            fullWidth
            disabled={!shouldOverrideOccupied}
          />
        </ParametersRowRight>
      </ParametersRowWrapper>
      <ParametersRowWrapper>
        <ParametersRowLeft>
          <FormControlLabel
            control={
              <StyledInputCheckbox checked={shouldOverrideVacant} onChange={setOverrideCheckbox} name="shouldOverrideVacant" value={shouldOverrideVacant} />
            }
            label={
              <p style={{ paddingLeft: "0.5em", marginBlockStart: "0", marginBlockEnd: "0", fontFamily: "Work Sans", fontSize: "14px" }}>
                Leases that end within Y months of
                {" "}
                <br />
                {" "}
                Calculation Date are considered either
              </p>
            }
            labelPlacement="end"
          />
          <div>
            <StyledRadioGroup
              value={isOverrideVacantIncluded}
              aria-label="vacant-included"
              name="isOverrideVacantIncluded"
              onChange={handleOverrideVacantRadioChange}
            >
              {isOverrideVacantIncludedRadioOptions.map(option => (
                <FormControlLabel
                  key={option.value}
                  value={option.value}
                  control={<Radio style={{ height: "30px" }} />}
                  label={<LabelText value={option.label} />}
                  disabled={!shouldOverrideVacant}
                />
              ))}
            </StyledRadioGroup>
          </div>
        </ParametersRowLeft>
        <ParametersRowRight>
          {getMonthErrorMessage("vacant")}
          <StyledInputNumber
            isError={!isMonthInputValid("vacant")}
            name="overrideVacantMonths"
            label="Number of months"
            variant="filled"
            value={overrideVacantMonths || ""}
            onChange={e => handleInputEntry(e, "integer")}
            fullWidth
            disabled={!shouldOverrideVacant}
          />
        </ParametersRowRight>
      </ParametersRowWrapper>
      <ParametersRowWrapper>
        <ParametersRowLeft>
          <FormControlLabel
            control={
              <StyledInputCheckbox
                checked={shouldReplaceRentRollStepByMonths}
                onChange={setOverrideCheckbox}
                name="shouldReplaceRentRollStepByMonths"
                value={shouldReplaceRentRollStepByMonths}
              />
            }
            label={
              <p style={{ paddingLeft: "0.5em", marginBlockStart: "0", marginBlockEnd: "0", fontFamily: "Work Sans", fontSize: "14px" }}>
                Replace rent with rent steps that begin
                {" "}
                <br />
                {" "}
                within x months.
              </p>
            }
            labelPlacement="end"
          />
        </ParametersRowLeft>
        <ParametersRowRight>
          {getMonthErrorMessage("replace")}
          <StyledInputNumber
            isError={!isMonthInputValid("replace")}
            name="replaceRentRollStepByMonths"
            label="Number of months"
            variant="filled"
            value={replaceRentRollStepByMonths || ""}
            onChange={e => handleInputEntry(e, "integer")}
            fullWidth
            disabled={!shouldReplaceRentRollStepByMonths}
          />
        </ParametersRowRight>
      </ParametersRowWrapper>
      <ParametersRowWrapper>
        <ParametersRowLeft>
          <FormControlLabel
            control={
              <StyledInputCheckbox
                checked={shouldUseFreeRentChargeCodeByMonths}
                onChange={setOverrideCheckbox}
                name="shouldUseFreeRentChargeCodeByMonths"
                value={shouldUseFreeRentChargeCodeByMonths}
              />
            }
            label={
              <p style={{ paddingLeft: "0.5em", marginBlockStart: "0", marginBlockEnd: "0", fontFamily: "Work Sans", fontSize: "14px" }}>
                Exclude free rent for x months. If checked,
                {" "}
                <br />
                {" "}
                select the free rent charge code in the
                {" "}
                <br />
                {" "}
                dropdown.
              </p>
            }
            labelPlacement="end"
          />
        </ParametersRowLeft>
        <ParametersRowRight>
          {getMonthErrorMessage("freeRent")}
          <StyledInputNumber
            isError={!isMonthInputValid("freeRent")}
            name="freeRentChargeCodeByMonths"
            label="Number of months"
            variant="filled"
            value={freeRentChargeCodeByMonths || ""}
            onChange={e => handleInputEntry(e, "integer")}
            fullWidth
            disabled={!shouldUseFreeRentChargeCodeByMonths}
          />
          {isChargeCodesLoading || isChargeCodesAliasesLoading ? (
            <div style={{ display: "flex", justifyContent: "center", width: "326px", height: "56px", alignItems: "center" }}>
              <CircularProgress size={20} thickness={2} />
            </div>
          ) : (
            <MultiSelectInput
              handleChange={onFreeRentChargeCodeSelectChange}
              variants={mapCodesToAliases(rentRollChargeCodes)}
              selectedVariants={mapCodesToAliases(selectedFreeRentChargeCodes)}
              disabled={!shouldUseFreeRentChargeCodeByMonths}
            />
          )}
        </ParametersRowRight>
      </ParametersRowWrapper>
      <ParametersRowWrapper>
        <ParametersRowLeft>Vacant Suite Preference</ParametersRowLeft>
        <ParametersRowRight>
          <InputTypeAhead
            variant="filled"
            id="vacantSuiteDefault"
            name="VacantSuiteDefault"
            label={!vacantSuiteDefault ? "Select Preference" : "Preference"}
            options={VACANT_SUITE_PREFERENCE_OPTIONS}
            displayWith={option => option.name}
            setValueWith={option => option.value}
            onChange={setVacantSuiteDefault}
            value={vacantSuiteDefault ? vacantSuiteDefault : ""}
          />
        </ParametersRowRight>
      </ParametersRowWrapper>
      <ParametersRowWrapper border={marketRentType === "manualEntry" && "none"}>
        <ParametersRowLeft>Market Rent for Vacant Suites</ParametersRowLeft>
        <ParametersRowRight>
          <InputTypeAhead
            variant="filled"
            id="calculationPeriod"
            name="Period"
            label={!marketRentType ? "Select Type" : "Type"}
            options={getMarketRentOptions()}
            displayWith={option => option.name}
            setValueWith={option => option.value}
            onChange={setMarketRentType}
            value={marketRentType}
          />
        </ParametersRowRight>
      </ParametersRowWrapper>
      {marketRentType === "manualEntry" && (
        <ParametersRowWrapper padding="0 1em 1em 1em">
          <ParametersRowLeft />
          <ParametersRowRight>
            <FormikInputMoney
              variant="filled"
              id="rentPerSF"
              name="manualEntryRentPerSqFt"
              label="Monthly Rent per Square Foot"
              fullWidth
              decimals={3}
              onChange={e => handleInputEntry(e, "money")}
              value={manualEntryRentPerSqFt}
            />
          </ParametersRowRight>
        </ParametersRowWrapper>
      )}
      <ParametersRowWrapper>
        <ParametersRowLeft>Include the following rent charges in the calculation</ParametersRowLeft>
        <ParametersRowRight>
          {isChargeCodesLoading || isChargeCodesAliasesLoading ? (
            <div style={{ display: "flex", justifyContent: "center", width: "326px", height: "56px", alignItems: "center" }}>
              <CircularProgress size={20} thickness={2} />
            </div>
          ) : (
            <MultiSelectInput
              handleChange={onChargeCodeSelectChange}
              variants={mapCodesToAliases(rentRollChargeCodes)}
              selectedVariants={mapCodesToAliases(selectedChargeCodes)}
            />
          )}
        </ParametersRowRight>
      </ParametersRowWrapper>
      <div style={{ display: "flex", justifyContent: "flex-end", padding: "1em" }}>
        <StyledPrimaryButton
          onClick={handleNext}
          size={ButtonSizeOptions.sm}
          label={isSavingField && !showConfirmationModal ? "Saving..." : "Save and Continue"}
          disabled={isNextDisabled}
        />
      </div>
    </VacancyRateWrapper>
  );
};

const mapStateToProps = ({
  lenderReportingDrawer: { allFilesForDeal }

}) => ({
  allFilesForDeal
});

// withFormik used only in order to use InputMoney component from LB CL
export default compose(
  connect(mapStateToProps, null),
  withFormik({})
)
(SelectCalculationParameters);
